import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Ship, TrendingUp, Cloud, DollarSign, Anchor, Map, CheckCircle, XCircle, Link as LinkIcon, Shield, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Maritime API integrations catalog
const INTEGRATIONS = [
  {
    id: 'marinetraffic',
    name: 'MarineTraffic',
    description: 'Real-time AIS vessel tracking and positions',
    category: 'Vessel Tracking',
    icon: Ship,
    website: 'https://www.marinetraffic.com/en/ais-api-services',
    pricing: 'Starting at $49/month',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your MarineTraffic API key' }
    ]
  },
  {
    id: 'aisstream',
    name: 'AIS Stream',
    description: 'Real-time AIS data via WebSocket',
    category: 'Vessel Tracking',
    icon: Ship,
    website: 'https://aisstream.io',
    pricing: 'Free tier available, then $20-200/month',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your AIS Stream API key' }
    ]
  },
  {
    id: 'vesselfinder',
    name: 'VesselFinder',
    description: 'Vessel tracking and fleet monitoring',
    category: 'Vessel Tracking',
    icon: Ship,
    website: 'https://www.vesselfinder.com/api',
    pricing: 'Custom pricing',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your VesselFinder API key' }
    ]
  },
  {
    id: 'shipandbunker',
    name: 'Ship & Bunker',
    description: 'Real-time bunker prices worldwide',
    category: 'Bunker Prices',
    icon: DollarSign,
    website: 'https://shipandbunker.com',
    pricing: 'Subscription required',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your Ship & Bunker API key' }
    ]
  },
  {
    id: 'mabux',
    name: 'MABUX',
    description: 'Global bunker prices and market data',
    category: 'Bunker Prices',
    icon: DollarSign,
    website: 'https://www.mabux.com',
    pricing: 'Free basic, paid API access',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your MABUX API key' }
    ]
  },
  {
    id: 'balticexchange',
    name: 'Baltic Exchange',
    description: 'Official freight indices (BDI, BCI, BPI)',
    category: 'Freight Rates',
    icon: TrendingUp,
    website: 'https://www.balticexchange.com',
    pricing: 'Enterprise pricing',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your Baltic Exchange API key' },
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Enter your Client ID' }
    ]
  },
  {
    id: 'xeneta',
    name: 'Xeneta',
    description: 'Container freight rate benchmarking',
    category: 'Freight Rates',
    icon: TrendingUp,
    website: 'https://www.xeneta.com',
    pricing: 'Custom pricing',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your Xeneta API key' }
    ]
  },
  {
    id: 'openweathermap',
    name: 'OpenWeatherMap',
    description: 'Marine weather forecasts and current conditions',
    category: 'Weather',
    icon: Cloud,
    website: 'https://openweathermap.org/api',
    pricing: 'Free tier, then $40-180/month',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your OpenWeatherMap API key' }
    ]
  },
  {
    id: 'stormglass',
    name: 'StormGlass',
    description: 'Marine weather, tides, and currents',
    category: 'Weather',
    icon: Cloud,
    website: 'https://stormglass.io',
    pricing: '$50-500/month',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your StormGlass API key' }
    ]
  },
  {
    id: 'fleetmon',
    name: 'FleetMon',
    description: 'Port intelligence and vessel tracking',
    category: 'Port Data',
    icon: Anchor,
    website: 'https://www.fleetmon.com',
    pricing: 'Custom pricing',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your FleetMon API key' }
    ]
  },
  {
    id: 'oceanbolt',
    name: 'Oceanbolt',
    description: 'Vessel movements and trade flows',
    category: 'Analytics',
    icon: TrendingUp,
    website: 'https://oceanbolt.com',
    pricing: 'Custom pricing',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your Oceanbolt API key' }
    ]
  },
  {
    id: 'mapbox',
    name: 'Mapbox',
    description: 'Interactive maps and vessel visualization',
    category: 'Maps',
    icon: Map,
    website: 'https://www.mapbox.com',
    pricing: 'Free tier 50K loads/month, then $5/1K',
    fields: [
      { key: 'access_token', label: 'Access Token', type: 'password', placeholder: 'Enter your Mapbox access token' }
    ]
  },
  {
    id: 'palantir_foundry',
    name: 'Palantir Foundry',
    description: 'Enterprise data integration platform - unified ontology for vessels, cargo, routes, and operations',
    category: 'Enterprise Analytics',
    icon: TrendingUp,
    website: 'https://www.palantir.com/platforms/foundry/',
    pricing: 'Enterprise - Contact Palantir',
    fields: [
      { key: 'tenant_url', label: 'Tenant URL', type: 'text', placeholder: 'https://your-tenant.palantirfoundry.com' },
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'OAuth 2.0 Client ID' },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'OAuth 2.0 Client Secret' },
      { key: 'api_token', label: 'API Token', type: 'password', placeholder: 'Bearer token (alternative to OAuth)' }
    ]
  },
  {
    id: 'palantir_gotham',
    name: 'Palantir Gotham',
    description: 'AI-powered intelligence platform - advanced analytics, pattern detection, and predictive modeling',
    category: 'Enterprise Analytics',
    icon: Shield,
    website: 'https://www.palantir.com/platforms/gotham/',
    pricing: 'Enterprise - Contact Palantir',
    fields: [
      { key: 'instance_url', label: 'Gotham Instance URL', type: 'text', placeholder: 'https://your-gotham-instance.com' },
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter your Gotham API key' },
      { key: 'workspace_id', label: 'Workspace ID', type: 'text', placeholder: 'Your workspace identifier' }
    ]
  },
  {
    id: 'palantir_apollo',
    name: 'Palantir Apollo',
    description: 'Continuous deployment platform - workflow automation and pipeline orchestration for maritime data',
    category: 'Enterprise Analytics',
    icon: Activity,
    website: 'https://www.palantir.com/platforms/apollo/',
    pricing: 'Enterprise - Contact Palantir',
    fields: [
      { key: 'apollo_url', label: 'Apollo URL', type: 'text', placeholder: 'https://apollo.palantirfoundry.com' },
      { key: 'service_token', label: 'Service Token', type: 'password', placeholder: 'Enter your Apollo service token' }
    ]
  },
  {
    id: 'palantir_aip',
    name: 'Palantir AIP (AI Platform)',
    description: 'Build and deploy AI agents and automations - LLM-powered assistants, custom AI functions, automated workflows',
    category: 'Enterprise Analytics',
    icon: TrendingUp,
    website: 'https://www.palantir.com/platforms/aip/',
    pricing: 'Enterprise - Contact Palantir',
    fields: [
      { key: 'foundry_url', label: 'Foundry URL', type: 'text', placeholder: 'https://your-tenant.palantirfoundry.com' },
      { key: 'aip_token', label: 'AIP API Token', type: 'password', placeholder: 'Bearer token for AIP APIs' },
      { key: 'agent_id', label: 'Agent ID (Optional)', type: 'text', placeholder: 'Pre-configured AIP Agent ID' },
      { key: 'model_access', label: 'Model Access Key', type: 'password', placeholder: 'LLM model access credentials' }
    ]
  }
];

export default function IntegrationsPage() {
  const [connections, setConnections] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [formData, setFormData] = useState({});
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/integrations`, {
        withCredentials: true
      });
      setConnections(response.data || {});
    } catch (error) {
      console.error('[Integrations] Error fetching connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (integration) => {
    setSelectedIntegration(integration);
    // Pre-fill existing data if already connected
    const existing = connections[integration.id];
    if (existing) {
      const prefilledData = {};
      integration.fields.forEach(field => {
        prefilledData[field.key] = existing.credentials?.[field.key] || '';
      });
      setFormData(prefilledData);
    } else {
      setFormData({});
    }
  };

  const handleDisconnect = async (integrationId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/integrations/${integrationId}`, {
        withCredentials: true
      });
      toast.success('Integration disconnected');
      await fetchConnections();
    } catch (error) {
      console.error('[Integrations] Error disconnecting:', error);
      toast.error('Failed to disconnect integration');
    }
  };

  const handleTestConnection = async () => {
    if (!selectedIntegration) return;
    
    setTesting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/integrations/${selectedIntegration.id}/test`,
        formData,
        { withCredentials: true }
      );
      toast.success('Connection test successful!');
    } catch (error) {
      console.error('[Integrations] Test failed:', error);
      toast.error('Connection test failed. Please check your credentials.');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!selectedIntegration) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/integrations/${selectedIntegration.id}`,
        formData,
        { withCredentials: true }
      );
      toast.success(`${selectedIntegration.name} connected successfully!`);
      setSelectedIntegration(null);
      setFormData({});
      await fetchConnections();
    } catch (error) {
      console.error('[Integrations] Error saving:', error);
      toast.error('Failed to save integration');
    }
  };

  const isConnected = (integrationId) => {
    return connections[integrationId]?.status === 'connected';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Vessel Tracking': return Ship;
      case 'Bunker Prices': return DollarSign;
      case 'Freight Rates': return TrendingUp;
      case 'Weather': return Cloud;
      case 'Port Data': return Anchor;
      case 'Analytics': return TrendingUp;
      case 'Maps': return Map;
      default: return LinkIcon;
    }
  };

  const groupedIntegrations = INTEGRATIONS.reduce((acc, integration) => {
    if (!acc[integration.category]) {
      acc[integration.category] = [];
    }
    acc[integration.category].push(integration);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <LinkIcon className="h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))] mx-auto" />
          <p className="text-[hsl(var(--muted-foreground))]">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="integrations-page">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
          Integrations
        </h1>
        <p className="text-[hsl(var(--muted-foreground))]">
          Connect your maritime data services using your own API keys
        </p>
      </div>

      {/* Info Banner */}
      <Card className="bg-[hsl(var(--accent))]/10 border-[hsl(var(--accent))]">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <LinkIcon className="h-6 w-6 text-[hsl(var(--accent))] mt-0.5" />
            <div>
              <h3 className="font-semibold mb-2">Bring Your Own API Keys</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                If you already have subscriptions to maritime data services, connect them here using your API keys. 
                Once connected, Maritime Pulse will fetch real-time data from these sources instead of using mock data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integrations by Category */}
      {Object.entries(groupedIntegrations).map(([category, integrations]) => (
        <div key={category} className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            {React.createElement(getCategoryIcon(category), { className: "h-5 w-5 text-[hsl(var(--accent))]" })}
            {category}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations.map((integration) => {
              const connected = isConnected(integration.id);
              const Icon = integration.icon;
              
              return (
                <Card key={integration.id} className={connected ? 'border-[hsl(var(--success))]' : ''} data-testid={`integration-${integration.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${connected ? 'bg-[hsl(var(--success))]/10' : 'bg-[hsl(var(--accent))]/10'}`}>
                          <Icon className={`h-5 w-5 ${connected ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--accent))]'}`} />
                        </div>
                        <div>
                          <CardTitle className="text-base">{integration.name}</CardTitle>
                        </div>
                      </div>
                      {connected ? (
                        <CheckCircle className="h-5 w-5 text-[hsl(var(--success))]" />
                      ) : (
                        <XCircle className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm min-h-[40px]">
                      {integration.description}
                    </CardDescription>
                    
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-[hsl(var(--muted-foreground))]">Pricing:</span>
                        <span className="font-medium">{integration.pricing}</span>
                      </div>
                      <div>
                        <Badge variant="outline" className="text-xs">
                          {connected ? 'Connected' : 'Not Connected'}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {connected ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleConnect(integration)}
                            data-testid={`edit-${integration.id}-btn`}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                            onClick={() => handleDisconnect(integration.id)}
                            data-testid={`disconnect-${integration.id}-btn`}
                          >
                            Disconnect
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                          onClick={() => handleConnect(integration)}
                          data-testid={`connect-${integration.id}-btn`}
                        >
                          Connect
                        </Button>
                      )}
                    </div>

                    <a
                      href={integration.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[hsl(var(--accent))] hover:underline flex items-center gap-1"
                    >
                      Learn more →
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {/* Connection Dialog */}
      <Dialog open={!!selectedIntegration} onOpenChange={(open) => !open && setSelectedIntegration(null)}>
        <DialogContent data-testid="connection-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedIntegration && React.createElement(selectedIntegration.icon, { className: "h-5 w-5 text-[hsl(var(--accent))]" })}
              Connect {selectedIntegration?.name}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect {selectedIntegration?.name} to Maritime Pulse
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedIntegration?.fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key}>{field.label}</Label>
                <Input
                  id={field.key}
                  type={field.type}
                  placeholder={field.placeholder}
                  value={formData[field.key] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                  data-testid={`input-${field.key}`}
                />
              </div>
            ))}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing}
              data-testid="test-connection-btn"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button
              onClick={handleSave}
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              data-testid="save-integration-btn"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
